/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Renders owner-authored rich text (HTML, from the owner-web RichTextEditor) safely. A built-in
 * allowlist sanitizer (no dependency) strips scripts/styles/iframes, event-handler attributes and
 * javascript: URLs, unwraps any non-allowed tag, and forces safe rel/target on links.
 */

import { useMemo } from 'react';

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'SPAN', 'DIV', 'HR', 'PRE', 'CODE',
]);
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'HEAD', 'TITLE', 'NOSCRIPT', 'TEMPLATE']);

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const process = (el: Element) => {
    // Clean descendants first (bottom-up), so unwrapping a parent keeps already-cleaned children.
    Array.from(el.children).forEach(process);

    const tag = el.tagName;
    const parent = el.parentNode;
    if (!ALLOWED_TAGS.has(tag)) {
      if (!parent) return;
      if (DROP_WITH_CONTENT.has(tag)) { parent.removeChild(el); return; }
      while (el.firstChild) parent.insertBefore(el.firstChild, el); // unwrap: keep text/children
      parent.removeChild(el);
      return;
    }
    // Allowed element: strip every attribute except <a href>, and never keep on* handlers.
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const keep = tag === 'A' && name === 'href';
      if (!keep || name.startsWith('on')) el.removeAttribute(attr.name);
    });
    if (tag === 'A') {
      const href = el.getAttribute('href') || '';
      if (/^\s*(javascript|data|vbscript):/i.test(href)) el.removeAttribute('href');
      el.setAttribute('rel', 'noopener noreferrer nofollow');
      el.setAttribute('target', '_blank');
    }
  };

  Array.from(doc.body.children).forEach(process);
  return doc.body.innerHTML;
}

/** True when the HTML has any visible text/content (ignores empty tags / whitespace). */
export function htmlHasContent(html?: string | null): boolean {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').trim().length > 0 || /<(img|hr|br)/i.test(html);
}

export default function RichHtml({ html, className = '' }: { html: string; className?: string }) {
  const clean = useMemo(() => sanitizeHtml(html), [html]);
  return <div className={`rich-html ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />;
}
